import { Loader } from "@devhop/ui";
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { usePOSContext } from "@/modules/pos/presentation/context/POSContext";
import { useBackgroundOfflineSync } from "@/modules/pos/presentation/lib/offline-sync/use-background-offline-sync";
import { useReferenceCacheRefresh } from "@/modules/pos/presentation/lib/offline-sync/use-reference-cache-refresh";
import { CartPanel } from "@/modules/pos/presentation/ui/CartPanel";
import { POSBottomBar } from "./POSBottomBar";
import { POSHeader } from "./POSHeader";
import { POSNavRail } from "./POSNavRail";

type POSLayoutProps = {
  children?: React.ReactNode;
  showHeader?: boolean;
};

export function POSLayout({ children, showHeader = true }: POSLayoutProps) {
  const { isLoading, isAuthenticated } = useAuthState();
  const { selection } = usePOSContext();
  const navigate = useNavigate({ from: "/app/pos" });
  const location = useLocation();
  const isCustomerDisplayPage =
    location.pathname === "/app/pos/customer-display";

  useReferenceCacheRefresh(selection?.branchId, selection?.deviceId, {
    includeVariantDetails: true,
  });
  useBackgroundOfflineSync(selection?.branchId, selection?.deviceId, {
    enabled: !isCustomerDisplayPage,
  });

  // Hide cart panel on orders pages
  const isPOSPage = location.pathname === "/app/pos";

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: "/auth/login" });
      return;
    }

    if (!isLoading && isAuthenticated && !selection) {
      navigate({ to: "/app/select-branch-device" });
    }
  }, [isLoading, isAuthenticated, selection, navigate]);

  if (isLoading) {
    return (
      <div className="container grid h-svh max-w-none items-center justify-center">
        <Loader />
      </div>
    );
  }

  // Customer display page should be full screen without header/bottom bar
  if (isCustomerDisplayPage) {
    return (
      <div className="relative flex h-svh flex-col overflow-hidden">
        {children ?? <Outlet />}
      </div>
    );
  }

  return (
    <div className="relative flex h-svh flex-col overflow-hidden bg-background">
      <div className="flex min-h-0 flex-1">
        <POSNavRail />

        <div className="flex min-w-0 flex-1 flex-col">
          {showHeader && <POSHeader />}
          <main className="min-h-0 flex-1 overflow-hidden">
            {children ?? <Outlet />}
          </main>
        </div>

        {isPOSPage && (
          <div className="hidden w-[400px] shrink-0 lg:block">
            <CartPanel />
          </div>
        )}
      </div>

      <POSBottomBar />
    </div>
  );
}
